import { Alert } from '@mui/joy';
import { Warning as WarningIcon } from '@phosphor-icons/react';
import { mergeProps } from '@react-aria/utils';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { StatusCodes } from 'http-status-codes';
import React, { ComponentProps } from 'react';
import { z } from 'zod';

import { getCurrentIntl } from '~/i18n/intl/intl';

const arrayAPIError = z.object({
  message: z.array(z.string()),
});
type ArrayAPIError = z.infer<typeof arrayAPIError>;

type RESTAPIError = {
  status: string;
  error: string;
  message: string;
  details: string;
  response: {
    status: string;
    statusText: string;
    data: object | undefined;
  };
};

function isRESTAPIError(maybeError: unknown): maybeError is RESTAPIError {
  if (
    maybeError &&
    typeof maybeError === 'object' &&
    (('code' in maybeError &&
      'message' in maybeError &&
      'response' in maybeError) ||
      ('status' in maybeError && 'message' in maybeError))
  ) {
    return true;
  }
  return false;
}

function isRESTAPIErrorArray(maybeError: unknown): maybeError is ArrayAPIError {
  return arrayAPIError.safeParse(maybeError).success;
}

const stringArrayErrors = z.array(z.string());
function isErrorStringArray(
  maybeError: unknown,
): maybeError is z.infer<typeof stringArrayErrors> {
  return stringArrayErrors.safeParse(maybeError).success;
}

const nestedErrorKey = z.object({ error: z.string().min(1) });
function isNestedErrorValue(
  maybeError: unknown,
): maybeError is z.infer<typeof nestedErrorKey> {
  return nestedErrorKey.safeParse(maybeError).success;
}

const recordErrors = z.record(
  z.string(),
  z.union([z.string(), z.array(z.string())]),
);
function isErrorRecord(
  maybeError: unknown,
): maybeError is z.infer<typeof recordErrors> {
  return recordErrors.safeParse(maybeError).success;
}

export const standardErrorSchema = z.object({
  type: z.string(),
  errors: z.array(
    z.object({
      code: z.string(),
      detail: z.string(),
      attr: z.string().nullable(),
    }),
  ),
});

export function isStandardError(
  maybeError: unknown,
): maybeError is z.infer<typeof standardErrorSchema> {
  return standardErrorSchema.safeParse(maybeError).success;
}

type ErrorStatusCodes = Extract<
  StatusCodes,
  400 | 401 | 403 | 404 | 500 | 504 | 502
>;

/** English fallback when intl returns the generic default (e.g. test env or uninitialized). */
const STATUS_MESSAGE_FALLBACK_EN: Record<ErrorStatusCodes, string> = {
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  500: 'Internal Server Error',
  504: 'Gateway Timeout',
  502: 'Bad Gateway',
};

/**
 * Cache for status error messages keyed by locale. Avoids recomputing
 * intl.formatMessage() on every error (micro-optimization since these
 * functions only run on error paths, but eliminates unnecessary work).
 */
const statusMapCache = new Map<string, Record<ErrorStatusCodes, string>>();

/**
 * Gets localized error status messages, with memoization by locale.
 * Returns a cached map for the current locale when available.
 */
function getDefaultErrorStatusMap(): Record<ErrorStatusCodes, string> {
  const intl = getCurrentIntl();
  const locale = intl.locale;

  const cached = statusMapCache.get(locale);
  if (cached) {
    return cached;
  }

  const map = {
    400: intl.formatMessage({ defaultMessage: 'Bad Request' }),
    401: intl.formatMessage({ defaultMessage: 'Unauthorized' }),
    403: intl.formatMessage({ defaultMessage: 'Forbidden' }),
    404: intl.formatMessage({ defaultMessage: 'Not Found' }),
    500: intl.formatMessage({ defaultMessage: 'Internal Server Error' }),
    504: intl.formatMessage({ defaultMessage: 'Gateway Timeout' }),
    502: intl.formatMessage({ defaultMessage: 'Bad Gateway' }),
  };

  statusMapCache.set(locale, map);
  return map;
}

/**
 * Type predicate to narrow an unknown error to `FetchBaseQueryError`
 */
export function isFetchBaseQueryError(
  error: unknown,
): error is FetchBaseQueryError {
  return typeof error === 'object' && error != null && 'status' in error;
}

export type GetErrorMessageOptions = { forSentry?: boolean };

/**
 * Try to extract message from standard error shape
 */
function tryGetStandardErrorMessage(error: FetchBaseQueryError | SerializedError): string | null {
  if (isFetchBaseQueryError(error) && isStandardError(error.data)) {
    const errorMessages = error.data.errors.map((serverError) => serverError.detail);
    if (errorMessages.length > 0) {
      return errorMessages.join('\r\n');
    }
  }
  return null;
}

/**
 * Try to extract message from deprecated error shapes
 */
function tryGetDeprecatedErrorMessage(
  error: FetchBaseQueryError | SerializedError,
  defaultMessage: string,
): string | null {
  if (!('status' in error)) {
    return null;
  }

  // Skip deprecated parsing for standard shape with empty errors
  const isStandardShapeWithEmptyErrors =
    isFetchBaseQueryError(error) &&
    isStandardError(error.data) &&
    error.data.errors.length === 0;

  if (isStandardShapeWithEmptyErrors) {
    return null;
  }

  if (isRESTAPIError(error.data)) {
    return error.data.message;
  }
  if (isErrorRecord(error.data)) {
    const message = Object.values(error.data).join(', ') || null;
    return message ?? defaultMessage;
  }
  if (isRESTAPIErrorArray(error.data)) {
    const message = error.data.message.find(Boolean);
    return message ?? defaultMessage;
  }
  if (isErrorStringArray(error.data)) {
    const message = error.data.find(Boolean);
    return message ?? defaultMessage;
  }
  if (isNestedErrorValue(error.data)) {
    const message = error.data.error || null;
    return message ?? defaultMessage;
  }

  return null;
}

/**
 * Try to extract status-based message (for Sentry grouping)
 */
function tryGetStatusMessage(
  error: FetchBaseQueryError | SerializedError,
  defaultMessage: string,
  options?: GetErrorMessageOptions,
): string | null {
  if (!isFetchBaseQueryError(error) || error.status === undefined) {
    return null;
  }

  const code = typeof error.status === 'number'
    ? error.status
    : Number.parseInt(String(error.status), 10);

  const statusMap = getDefaultErrorStatusMap();
  if (!Number.isInteger(code) || !(code in statusMap)) {
    return null;
  }

  if (options?.forSentry) {
    return STATUS_MESSAGE_FALLBACK_EN[code as ErrorStatusCodes];
  }

  return defaultMessage;
}

export const getErrorMessage = (
  error: FetchBaseQueryError | SerializedError,
  defaultMessage?: string,
  options?: GetErrorMessageOptions,
): string => {
  // For Sentry, use English fallback to ensure consistent error grouping
  const fallbackMessage =
    defaultMessage ??
    (options?.forSentry
      ? 'Error encountered. We\'re currently fixing it.'
      : getCurrentIntl().formatMessage({
        defaultMessage: 'Error encountered. We\'re currently fixing it.',
      }));

  // Try standard error shape
  const standardMsg = tryGetStandardErrorMessage(error);
  if (standardMsg !== null) {
    return standardMsg;
  }

  // Try deprecated error shapes
  const deprecatedMsg = tryGetDeprecatedErrorMessage(error, fallbackMessage);
  if (deprecatedMsg !== null) {
    return deprecatedMsg;
  }

  // Try status-based message (for Sentry)
  const statusMsg = tryGetStatusMessage(error, fallbackMessage, options);
  if (statusMsg !== null) {
    return statusMsg;
  }

  return fallbackMessage;
};

/**
 * Type predicate to narrow an unknown error to an object with a string 'message' property
 */
export function isErrorWithMessage(error: unknown): error is {
  status: number;
  data: {
    error: string;
    message: string;
    status: number;
  };
} {
  return (
    isFetchBaseQueryError(error) &&
    'data' in error &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof (error.data as any).message === 'string'
  );
}

/**
 * Type predicate to narrow an unknown error to an object with a string 'message' property
 */
export function isMutationError(error: unknown): error is {
  error: {
    status: number;
    data: {
      error: string;
      message: string;
      status: number;
    };
  };
} {
  return (
    typeof error === 'object' &&
    error != null &&
    'error' in error &&
    isErrorWithMessage(error.error)
  );
}

/**
 * Type predicate to narrow an unknown error to an object with a string 'message' property
 */
export function isHTTPRequestError(error: unknown): error is {
  status: number;
  data: {
    error: string;
    message: string;
    status: number;
  };
} {
  return (
    isFetchBaseQueryError(error) &&
    'data' in error &&
    'status' in error &&
    typeof error.status === 'number' &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof (error.data as any).message === 'string'
  );
}

export function RequestErrorBanner({
  error,
  title,
}: Readonly<{
  title?: React.ReactNode;
  error?: FetchBaseQueryError | SerializedError;
}>) {
  if (!error) {
    return null;
  }

  const message = getErrorMessage(error);

  return (
    <Alert
      startDecorator={<WarningIcon weight='fill' />}
      variant='soft'
      color='danger'
    >
      <div className='flex flex-col'>
        {title ? <div className='text-sm/6 font-medium'>{title}</div> : null}
        <p className='text-sm/6 font-normal whitespace-pre-line'>{message}</p>
      </div>
    </Alert>
  );
}

export function StatusCodeErrorBanner({
  error,
  statusCode,
  statusMap,
  size = 'md',
}: Readonly<{
  error?: FetchBaseQueryError | SerializedError;
  statusCode?: StatusCodes;
  statusMap?: Partial<Record<ErrorStatusCodes, string>>;
}> &
  Pick<ComponentProps<typeof Alert>, 'size'>) {
  if (!error) {
    return null;
  }

  const mergedMap: Record<number | string, string> = mergeProps(
    getDefaultErrorStatusMap(),
    statusMap ?? {},
  );
  const code = isHTTPRequestError(error) ? error.status : statusCode;
  const isKnownCode = !!(code && code in mergedMap);

  if (!code || !isKnownCode) {
    return (
      <Alert
        size={size}
        startDecorator={<WarningIcon weight='fill' />}
        variant='soft'
        color='danger'
      >
        <p className='text-sm/6 font-normal whitespace-pre-line'>
          {getErrorMessage(error)}
        </p>
      </Alert>
    );
  }
  const message = mergedMap[code];

  return (
    <Alert
      startDecorator={<WarningIcon weight='fill' />}
      variant='soft'
      color='danger'
    >
      {message}
    </Alert>
  );
}
